import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Check, Copy, ExternalLink, Radio, Square } from 'lucide-react'
import { liveApi, type LiveActivitySummary, type LiveDeliveryDto, type LiveQuestionSummary, type LiveTarget } from '@/api/live'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useAdminDates, useTrainerDates } from '@/hooks/useDates'
import { useLiveStatus } from './useLiveStatus'

function errorMessage(err: unknown): string {
  const data = (err as { response?: { data?: { detail?: string; title?: string } } })?.response?.data
  return data?.detail ?? data?.title ?? 'Something went wrong. Please try again.'
}

const TYPE_LABEL: Record<string, string> = { quiz: 'Quiz', confidence: 'Confidence check', wordcloud: 'Word cloud', qa: 'Q&A', pulse: 'Understanding check' }

const PULSE_ROWS = [
  { key: 'green', label: 'With you', bar: 'bg-green-500' },
  { key: 'amber', label: 'Not sure', bar: 'bg-amber-400' },
  { key: 'red', label: 'Lost', bar: 'bg-red-500' },
] as const

/** Every round of a pulse side by side, so it shows whether understanding improved through the session. */
function PulseRounds({ rounds }: { rounds: { green: number; amber: number; red: number }[] }) {
  if (rounds.length === 0) return <p className="text-sm text-gray-400">No answers.</p>
  return (
    <div className="space-y-3">
      {rounds.map((r, i) => {
        const total = Math.max(1, r.green + r.amber + r.red)
        return (
          <div key={i} className="space-y-1">
            <p className="text-xs text-gray-500">{rounds.length > 1 ? `Round ${i + 1}` : 'Result'} · {r.green + r.amber + r.red} answered</p>
            <div className="flex h-3 rounded-full overflow-hidden bg-gray-100" aria-hidden="true">
              {PULSE_ROWS.map(p => <div key={p.key} className={p.bar} style={{ width: `${(r[p.key] / total) * 100}%` }} />)}
            </div>
            <p className="text-xs text-gray-600">{PULSE_ROWS.map(p => `${r[p.key]} ${p.label.toLowerCase()}`).join(' · ')}</p>
          </div>
        )
      })}
    </div>
  )
}

function Tile({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone?: 'good' }) {
  return (
    <div className={`rounded-lg border px-3 py-2 ${tone === 'good' ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-gray-50'}`}>
      <p className="text-xs text-gray-500">{label}</p>
      <p className={`text-lg font-semibold ${tone === 'good' ? 'text-green-700' : 'text-gray-900'}`}>{value}</p>
      {sub && <p className="text-xs text-gray-400">{sub}</p>}
    </div>
  )
}

function Headline({ delivery }: { delivery: LiveDeliveryDto }) {
  const h = delivery.headline
  if (!h) return null

  // Prefer the like-for-like comparison (people who did both) when there are enough of them to mean something.
  const paired = (h.pairedCount ?? 0) >= 3 && h.pairedPreAveragePercent !== null && h.pairedPostAveragePercent !== null
  const pre = paired ? h.pairedPreAveragePercent : h.preAveragePercent
  const post = paired ? h.pairedPostAveragePercent : h.postAveragePercent
  const gain = pre !== null && post !== null ? Math.round((post - pre) * 10) / 10 : null

  const confPaired = (h.confidencePairedCount ?? 0) >= 3 && h.confidencePairedBeforeAverage !== null && h.confidencePairedAfterAverage !== null
  const before = confPaired ? h.confidencePairedBeforeAverage : h.confidenceBeforeAverage
  const after = confPaired ? h.confidencePairedAfterAverage : h.confidenceAfterAverage
  const confGain = before !== null && after !== null ? Math.round((after - before) * 10) / 10 : null

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
      {gain !== null && (
        <Tile
          label="Learning gain"
          value={`${gain >= 0 ? '+' : ''}${gain} pts`}
          sub={`${pre}% → ${post}%${paired ? ` (${h.pairedCount} who did both)` : ''}`}
          tone={gain > 0 ? 'good' : undefined}
        />
      )}
      {pre !== null && post === null && <Tile label="Pre-course average" value={`${pre}%`} />}
      {pre === null && post !== null && <Tile label="Post-course average" value={`${post}%`} />}
      {confGain !== null && (
        <Tile
          label="Confidence"
          value={`${confGain >= 0 ? '+' : ''}${confGain}`}
          sub={`${before} → ${after} out of 10${confPaired ? ` (${h.confidencePairedCount} who did both)` : ''}`}
          tone={confGain > 0 ? 'good' : undefined}
        />
      )}
      {before !== null && after === null && <Tile label="Confidence before" value={`${before} / 10`} />}
      {before === null && after !== null && <Tile label="Confidence after" value={`${after} / 10`} />}
      {h.feedbackAverage !== null && (
        <Tile label="Feedback" value={`${h.feedbackAverage} / 10`} sub={`${h.feedbackCount ?? 0} response${h.feedbackCount === 1 ? '' : 's'}`} />
      )}
    </div>
  )
}

function QuestionResults({ q, hideTitle = false }: { q: LiveQuestionSummary; hideTitle?: boolean }) {
  const max = Math.max(1, ...q.options.map(o => o.count), ...q.distribution)
  return (
    <div className="space-y-1.5">
      {!hideTitle && <p className="text-sm text-gray-800">{q.text}</p>}
      {(q.type === 'choice' || q.type === 'multiple') && (
        <>
          {q.options.map((o, i) => {
            const right = q.type === 'multiple' ? !!q.correctIndexes?.includes(i) : q.correctIndex === i
            return (
              <div key={i}>
                <div className="flex justify-between text-xs text-gray-600">
                  <span>{o.text}{right ? ' ✓' : ''}</span><span className="font-semibold">{o.count}</span>
                </div>
                <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
                  <div className={right ? 'h-full bg-green-500' : 'h-full bg-indigo-400'} style={{ width: `${(o.count / max) * 100}%` }} />
                </div>
              </div>
            )
          })}
          {q.type === 'multiple' && (
            <p className="text-xs text-gray-500">
              Each bar is how many people ticked that option.
              {q.fullyCorrect != null && <> <span className="font-semibold text-gray-800">{q.fullyCorrect}</span> of {q.totalResponses} ticked exactly the right ones.</>}
            </p>
          )}
        </>
      )}
      {q.type === 'rating' && (
        <>
          <p className="text-xs text-gray-500">Average <span className="font-semibold text-gray-800">{q.average ?? '–'}</span> / 10 · {q.totalResponses} response{q.totalResponses === 1 ? '' : 's'}</p>
          <div className="flex items-end gap-1 h-12">
            {q.distribution.map((c, i) => (
              <div key={i} className="flex-1 flex flex-col justify-end items-center h-full text-[10px] text-gray-400">
                <div className="w-full bg-indigo-400 rounded-t" style={{ height: `${(c / max) * 100}%`, minHeight: 2 }} />{i + 1}
              </div>
            ))}
          </div>
        </>
      )}
      {q.type === 'text' && (
        <ul className="list-disc pl-5 space-y-0.5 text-sm text-gray-700">
          {q.answers.length === 0 && <li className="list-none -ml-5 text-gray-400">No comments.</li>}
          {q.answers.map((a, i) => <li key={i}>{a}</li>)}
        </ul>
      )}
    </div>
  )
}

function ActivityResults({ activity }: { activity: LiveActivitySummary }) {
  const detail =
    activity.type === 'qa' ? `${activity.questions.length} question${activity.questions.length === 1 ? '' : 's'}` :
    activity.type === 'pulse' ? `${activity.rounds.length} round${activity.rounds.length === 1 ? '' : 's'} · ${activity.submitted} answers` :
    `${activity.submitted} submitted` +
      (activity.type === 'quiz' && activity.scorePercent !== null ? ` · average ${activity.scorePercent}%` : '') +
      (activity.type === 'confidence' && activity.average !== null ? ` · average ${activity.average} / 10` : '')

  return (
    <details className="rounded-lg border border-gray-200">
      <summary className="cursor-pointer px-3 py-2 text-sm font-medium text-gray-900 flex flex-wrap items-center gap-x-3">
        {activity.name}
        <span className="text-xs font-normal text-gray-500">{'adhoc' in activity && activity.adhoc ? 'Quick poll' : TYPE_LABEL[activity.type]} · {detail}</span>
      </summary>
      <div className="px-3 pb-3 pt-1 space-y-4">
        {(activity.type === 'quiz' || activity.type === 'confidence') && activity.questions.map(q => <QuestionResults key={q.id} q={q} hideTitle={!!activity.adhoc} />)}
        {activity.type === 'wordcloud' && (
          <div className="space-y-1.5">
            {activity.prompt && <p className="text-sm text-gray-800">{activity.prompt}</p>}
            {activity.words.length === 0 ? <p className="text-sm text-gray-400">No words.</p> : (
              <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
                {activity.words.map(w => (
                  <span key={w.word} className="font-semibold text-indigo-600" style={{ fontSize: `${0.9 + (w.count / Math.max(1, activity.words[0].count)) * 1.4}rem` }}>
                    {w.word}{w.count > 1 && <sup className="text-[10px] text-gray-400 ml-0.5">{w.count}</sup>}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
        {activity.type === 'pulse' && (
          <div className="space-y-1.5">
            {activity.prompt && <p className="text-sm text-gray-800">{activity.prompt}</p>}
            <PulseRounds rounds={activity.rounds} />
          </div>
        )}
        {activity.type === 'qa' && (
          <ul className="space-y-1.5">
            {activity.questions.length === 0 && <li className="text-sm text-gray-400">No questions.</li>}
            {[...activity.questions].sort((a, b) => b.votes - a.votes).map((q, i) => (
              <li key={i} className="text-sm text-gray-700 flex gap-2">
                <span className="text-xs font-semibold text-gray-500 w-8 shrink-0">▲ {q.votes}</span>
                <span>{q.text}{q.answered && <span className="text-xs text-green-600"> · answered</span>}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </details>
  )
}

function Results({ delivery }: { delivery: LiveDeliveryDto }) {
  const summary = delivery.summary
  if (!summary) {
    return <p className="text-sm text-gray-500">This room was closed without results being reported.</p>
  }
  const activities = summary.activities ?? []
  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">
        <span className="font-semibold text-gray-900">{summary.participantCount}</span> participant{summary.participantCount === 1 ? '' : 's'} joined
      </p>
      <Headline delivery={delivery} />
      {activities.length > 0 && <div className="space-y-2">{activities.map(a => <ActivityResults key={a.id} activity={a} />)}</div>}
      {summary.polls.length === 0 && activities.length === 0 && <p className="text-sm text-gray-400">No polls or activities were run.</p>}
      {summary.polls.map(p => {
        const max = Math.max(1, ...p.options.map(o => o.count))
        return (
          <div key={p.id} className="space-y-2">
            <p className="text-sm font-medium text-gray-900">{p.question}</p>
            {p.kind === 'choice' ? (
              <div className="space-y-1.5">
                {p.options.map(o => (
                  <div key={o.text}>
                    <div className="flex justify-between text-xs text-gray-600"><span>{o.text}</span><span className="font-semibold">{o.count}</span></div>
                    <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                      <div className="h-full bg-indigo-500" style={{ width: `${(o.count / max) * 100}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <ul className="list-disc pl-5 space-y-0.5 text-sm text-gray-700">
                {p.answers.length === 0 && <li className="list-none -ml-5 text-gray-400">No answers.</li>}
                {p.answers.map((a, i) => <li key={i}>{a}</li>)}
              </ul>
            )}
            <p className="text-xs text-gray-400">{p.totalResponses} response{p.totalResponses === 1 ? '' : 's'}</p>
          </div>
        )
      })}
    </div>
  )
}

/**
 * CertSlate Live: start a live room (QR/code join, polls) for a booking or session, reopen the
 * presenter console, and see the results afterwards. Renders nothing unless Live is enabled for
 * the tenant (and `hidden` is false, e.g. for a cancelled booking).
 */
const ENDED_FORMAT: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' }

function AdminEndedAt({ at }: { at: string }) {
  const { formatDateTime } = useAdminDates()
  return <>{formatDateTime(at, ENDED_FORMAT)}</>
}

function TrainerEndedAt({ at }: { at: string }) {
  const { formatDateTime } = useTrainerDates()
  return <>{formatDateTime(at, ENDED_FORMAT)}</>
}

/** When the session ended, in the tenant's region and time zone (the admin and trainer apps load those differently). */
function EndedAt({ at, audience }: { at: string; audience: 'admin' | 'trainer' }) {
  return audience === 'trainer' ? <TrainerEndedAt at={at} /> : <AdminEndedAt at={at} />
}

export function LiveRoomCard({ target, hidden = false, audience = 'admin' }: { target: LiveTarget; hidden?: boolean; audience?: 'admin' | 'trainer' }) {
  const queryClient = useQueryClient()
  const queryKey = ['live-status', target.bookingId ?? target.sessionId]
  const [busy, setBusy] = useState<'start' | 'console' | 'end' | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  const { data } = useLiveStatus(target, !hidden)

  if (hidden || !data?.enabled) return null
  const delivery = data.delivery
  const isOpen = delivery?.status === 'Open'

  // Open the console in a tab opened *synchronously* from the click — mobile Safari silently blocks
  // window.open() once an await has happened — then point it at the real link when we have it.
  // (No 'noopener' on the placeholder: it makes window.open return null, so we couldn't navigate it.)
  const openConsole = async (kind: 'start' | 'console', getUrl: () => Promise<string>) => {
    setError(null)
    setBusy(kind)
    const win = window.open('', '_blank')
    try {
      const url = await getUrl()
      if (win) win.location.href = url
      else window.location.href = url
    } catch (err) {
      win?.close()
      setError(errorMessage(err))
    } finally {
      setBusy(null)
      queryClient.invalidateQueries({ queryKey })
    }
  }

  const start = () => openConsole('start', async () => (await liveApi.launch(target)).data.presenterUrl)
  const reopen = () => openConsole('console', async () => (await liveApi.presenterLink(delivery!.id)).data.presenterUrl)

  const end = async () => {
    if (!delivery || !confirm('End this live session? Attendees will be disconnected and the results saved here.')) return
    setError(null)
    setBusy('end')
    try {
      await liveApi.end(delivery.id)
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setBusy(null)
      queryClient.invalidateQueries({ queryKey })
    }
  }

  const copy = () => {
    if (!delivery) return
    navigator.clipboard.writeText(delivery.joinUrl)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Radio size={16} className={isOpen ? 'text-green-600' : 'text-gray-400'} />
          Live
          {isOpen && <span className="text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-full px-2 py-0.5">Room open</span>}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {isOpen && delivery ? (
          <>
            <div className="flex flex-wrap items-center gap-x-8 gap-y-3">
              <div>
                <p className="text-xs text-gray-500">Join code</p>
                <p className="text-3xl font-bold tracking-widest text-indigo-700 tabular-nums">{delivery.joinCode}</p>
              </div>
              <div className="min-w-0">
                <p className="text-xs text-gray-500">Join link</p>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-800 break-all">{delivery.joinUrl}</span>
                  <button onClick={copy} className="text-gray-400 hover:text-gray-700 cursor-pointer shrink-0" title="Copy join link">
                    {copied ? <Check size={15} /> : <Copy size={15} />}
                  </button>
                </div>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button type="button" size="sm" className="gap-1.5" onClick={reopen} disabled={busy !== null}>
                <ExternalLink size={14} /> {busy === 'console' ? 'Opening…' : 'Open presenter console'}
              </Button>
              <Button type="button" size="sm" variant="outline" className="gap-1.5" onClick={end} disabled={busy !== null}>
                <Square size={13} /> {busy === 'end' ? 'Ending…' : 'End session'}
              </Button>
            </div>
            <p className="text-xs text-gray-400">Attendees join on their phones — no sign-in. Share the code, or show the QR code from the presenter console.</p>
          </>
        ) : (
          <>
            {delivery ? (
              <div className="space-y-2">
                <p className="text-xs text-gray-500">Last live session{delivery.endedAt && <> — ended <EndedAt at={delivery.endedAt} audience={audience} /></>}</p>
                <Results delivery={delivery} />
              </div>
            ) : (
              <p className="text-sm text-gray-500">Run live polls with your group. Attendees join on their phones with a QR code — no accounts, no app.</p>
            )}
            <Button type="button" size="sm" className="gap-1.5" onClick={start} disabled={busy !== null}>
              <Radio size={14} /> {busy === 'start' ? 'Starting…' : delivery ? 'Start a new live session' : 'Start Live'}
            </Button>
          </>
        )}
        {error && <p className="text-sm text-red-600">{error}</p>}
      </CardContent>
    </Card>
  )
}
